import { useNavigate } from "react-router-dom";
import { Flex } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAlert } from "@/shared/hooks";
import { AppButton, AppLink, AppText } from "@/shared/Components";
import { CloseCrossIcon } from "@/shared/icons";
import { appRouterPaths } from "@/services/navigation";
import { useGetProfileQuery } from "@/services/api/entities/profile";
import { AuthStatus, useGetStateQuery } from "@/services/api";
import { useNotification } from "@/shared/hooks/use-notification";
import { useAuth } from "@/services/auth";

const EMAIL_KEY = "confirmEmailNotification";
const READONLY_KEY = "readonlyNotification";

export const useGlobalNotifications = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();

  const { data: profile, isSuccess: profileIsSuccess } = useGetProfileQuery();
  const { data: stateResponse, isSuccess: stateIsSuccess } = useGetStateQuery();

  const { callInfo, destroy, destroyAll } = useAlert();
  const { callInfoNotification, destroyNotification, destroyAllNotification } = useNotification();

  const emailMessageContent = () => {
    return (
      <Flex align="center" gap={12} justify="space-between">
        <AppLink
          onClick={() => {
            navigate(appRouterPaths.settings);
            destroy(EMAIL_KEY);
          }}
          style={{ color: "var(--text-light-solid)" }}
        >
          {t("settingsPage.general.confirmEmailNotification")}
        </AppLink>
        <CloseCrossIcon
          onClick={() => destroy(EMAIL_KEY)}
          style={{ cursor: "pointer" }}
          stroke="var(--text-light-solid)"
        />
      </Flex>
    );
  };

  const readonlyMessageContent = () => {
    return (
      <Flex align="center" gap={12} justify="space-between">
        <AppText>{t("settingsPage.general.subscription.readonlyMessage")}</AppText>
        <AppButton
          type="primary"
          onClick={() => {
            navigate(appRouterPaths.renewSubscription, { state: { fromSettings: true } });
            destroyNotification(READONLY_KEY);
          }}
        >
          {t("settingsPage.general.subscription.renewSubscription")}
        </AppButton>
      </Flex>
    );
  };

  useEffect(() => {
    if (stateResponse?.state === AuthStatus.READONLY && stateIsSuccess) {
      callInfoNotification({
        text: readonlyMessageContent(),
        duration: 0,
        key: READONLY_KEY,
        placement: "bottomRight",
        closable: false,
      });
    }
    if (stateResponse?.state !== AuthStatus.READONLY && stateIsSuccess) {
      destroyNotification(READONLY_KEY);
    }
  }, [stateIsSuccess, stateResponse?.state]);

  useEffect(() => {
    if (!profile?.emailConfirmed && profileIsSuccess) {
      callInfo({
        text: emailMessageContent(),
        duration: 0,
        key: EMAIL_KEY,
      });
    }
    if (profile?.emailConfirmed && profileIsSuccess) {
      destroyNotification(EMAIL_KEY);
    }
  }, [profileIsSuccess, profile, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      destroyAllNotification();
      destroyAll();
    }
  }, [isAuthenticated]);
};
